import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiErrorDetail, fmtMoney } from "@/lib/api";
import { Bell, Save, X, Loader2, ExternalLink, LogOut, DoorOpen } from "lucide-react";

const STRATEGIES = [
  { v: "wholesale", l: "Wholesale" },
  { v: "flip", l: "Fix & Flip" },
  { v: "brrrr", l: "BRRRR" },
  { v: "rental", l: "Buy & Hold" },
  { v: "novation", l: "Novation" },
  { v: "sub_to", l: "Sub-To" },
  { v: "seller_finance", l: "Seller Finance" },
];

/**
 * Buyer's magic-link authed dashboard. Landed on here via a token in email:
 * /buyers/me?t=<jwt>.
 *
 * Provides: view profile, edit criteria, review recent matches, unsubscribe.
 */
export default function BuyerDashboard() {
  const [params] = useSearchParams();
  const token = params.get("t") || params.get("token");
  const [buyer, setBuyer] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [unsubscribing, setUnsubscribing] = useState(false);

  useEffect(() => {
    if (!token) { setError("This link is missing a token. Ask the wholesaler to resend."); setLoading(false); return; }
    axios.get(`${API}/public/buyers/me`, { params: { token } })
      .then((r) => {
        setBuyer(r.data.buyer);
        setMatches(r.data.matches || []);
        setForm({
          name: r.data.buyer.name || "",
          phone: r.data.buyer.phone || "",
          states_text: (r.data.buyer.criteria?.states || []).join(", "),
          cities_text: (r.data.buyer.criteria?.cities || []).join(", "),
          zips_text: (r.data.buyer.criteria?.zip_codes || []).join(", "),
          price_min: r.data.buyer.criteria?.price_min ?? "",
          price_max: r.data.buyer.criteria?.price_max ?? "",
          min_beds: r.data.buyer.criteria?.min_beds ?? "",
          strategies: r.data.buyer.criteria?.strategies || [],
          notify_sms: !!r.data.buyer.criteria?.notify_sms,
        });
      })
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || "This link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const criteria = {
        states: form.states_text.split(",").map(s => s.trim().toUpperCase()).filter(Boolean),
        cities: form.cities_text.split(",").map(s => s.trim()).filter(Boolean),
        zip_codes: form.zips_text.split(",").map(s => s.trim()).filter(Boolean),
        price_min: form.price_min ? Number(form.price_min) : undefined,
        price_max: form.price_max ? Number(form.price_max) : undefined,
        min_beds: form.min_beds ? Number(form.min_beds) : undefined,
        strategies: form.strategies,
        notify_sms: !!form.notify_sms,
      };
      const { data } = await axios.patch(`${API}/public/buyers/me`, {
        name: form.name,
        phone: form.phone || null,
        criteria,
      }, { params: { token } });
      setBuyer(data.buyer);
      toast.success("Criteria saved ✅");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const unsubscribe = async () => {
    if (!confirm("Unsubscribe from all deal alerts on this list? You can always sign up again.")) return;
    setUnsubscribing(true);
    try {
      await axios.post(`${API}/public/buyers/unsubscribe`, {}, { params: { token } });
      setBuyer((b) => ({ ...b, unsubscribed: true }));
      toast.success("You&apos;re unsubscribed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Unsubscribe failed");
    } finally {
      setUnsubscribing(false);
    }
  };

  const toggleStrategy = (v) => setForm((f) => ({ ...f, strategies: f.strategies.includes(v) ? f.strategies.filter(x => x !== v) : [...f.strategies, v] }));

  if (loading) return <Screen><Loader2 size={20} className="animate-spin" /> Loading…</Screen>;
  if (error) return <Screen>{error}</Screen>;
  if (!buyer || !form) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[var(--ink,#0F172A)]" data-testid="buyer-dashboard">
      <header className="border-b border-[var(--line,#E2E8F0)] bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-[var(--gold,#F59E0B)]" />
            <span className="font-display font-extrabold text-lg">Your Deal Alerts</span>
          </div>
          <Link to="/" className="text-xs text-[var(--ink-3,#94A3B8)] hover:text-[var(--ink,#0F172A)]">Powered by iSpeedToClose →</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight">Hi {buyer.name || "there"} 👋</h1>
          <div className="text-sm text-[var(--ink-2,#475569)] mt-1">Signed in as <b>{buyer.email}</b></div>
          {buyer.unsubscribed && (
            <div className="mt-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2">
              You&apos;re currently unsubscribed. Update criteria and save to re-subscribe.
            </div>
          )}
        </div>

        {/* Criteria editor */}
        <section className="rounded-xl border border-[var(--line,#E2E8F0)] bg-white p-6 space-y-4" data-testid="buyer-criteria-form">
          <div className="text-xs uppercase tracking-widest text-[var(--ink-3,#94A3B8)]">Your strike zone</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="buyer-field-name" />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="buyer-field-phone" />
          </div>
          <Field label="Target states (comma-separated)" placeholder="FL, GA, TN" value={form.states_text} onChange={(v) => setForm({ ...form, states_text: v })} testid="buyer-field-states" />
          <Field label="Cities (optional)" placeholder="Tampa, Orlando" value={form.cities_text} onChange={(v) => setForm({ ...form, cities_text: v })} testid="buyer-field-cities" />
          <Field label="ZIP codes (optional)" placeholder="33612, 33610" value={form.zips_text} onChange={(v) => setForm({ ...form, zips_text: v })} testid="buyer-field-zips" />
          <div className="grid grid-cols-3 gap-3">
            <Field label="Min price $" type="number" value={form.price_min} onChange={(v) => setForm({ ...form, price_min: v })} testid="buyer-field-pricemin" />
            <Field label="Max price $" type="number" value={form.price_max} onChange={(v) => setForm({ ...form, price_max: v })} testid="buyer-field-pricemax" />
            <Field label="Min beds" type="number" value={form.min_beds} onChange={(v) => setForm({ ...form, min_beds: v })} testid="buyer-field-minbeds" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3,#94A3B8)] mb-1">Exit strategies</div>
            <div className="flex flex-wrap gap-1.5">
              {STRATEGIES.map((s) => {
                const active = form.strategies.includes(s.v);
                return (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => toggleStrategy(s.v)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${active ? "bg-[var(--ink,#0F172A)] text-white border-[var(--ink,#0F172A)]" : "bg-white text-[var(--ink-2,#475569)] border-[var(--line,#E2E8F0)]"}`}
                    data-testid={`buyer-field-strategy-${s.v}`}
                  >{s.l}</button>
                );
              })}
            </div>
          </div>
          <label className="flex items-start gap-2 text-sm text-[var(--ink-2,#475569)] leading-relaxed cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.notify_sms}
              onChange={(e) => setForm({ ...form, notify_sms: e.target.checked })}
              disabled={!form.phone}
              className="mt-0.5 h-4 w-4 rounded border-[var(--line,#E2E8F0)] accent-[var(--ink,#0F172A)] shrink-0"
              data-testid="buyer-field-notify-sms"
            />
            <span>
              Also text me when a matching deal drops.{" "}
              {!form.phone && <span className="text-[var(--ink-3,#94A3B8)]">(Add a phone number above to enable)</span>}
              {form.phone && <span className="text-[var(--ink-3,#94A3B8)]">Msg &amp; data rates may apply · reply STOP any time.</span>}
            </span>
          </label>
          <div className="flex items-center gap-3 pt-2 border-t border-[var(--line,#E2E8F0)]">
            <button type="button" className="btn-money" onClick={save} disabled={saving} data-testid="buyer-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : "Save criteria"}
            </button>
            {!buyer.unsubscribed && (
              <button type="button" className="btn-ghost text-xs" onClick={unsubscribe} disabled={unsubscribing} data-testid="buyer-unsubscribe">
                <LogOut size={12} />
                {unsubscribing ? "Unsubscribing…" : "Unsubscribe"}
              </button>
            )}
          </div>
        </section>

        {/* Recent matches */}
        <section>
          <div className="text-xs uppercase tracking-widest text-[var(--ink-3,#94A3B8)] mb-2">Recent matches</div>
          {matches.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[var(--line,#E2E8F0)] bg-white p-6 text-center text-sm text-[var(--ink-3,#94A3B8)]" data-testid="buyer-matches-empty">
              No matches yet — we&apos;ll email you the moment a new deal fits your criteria.
            </div>
          ) : (
            <div className="space-y-2" data-testid="buyer-matches-list">
              {matches.map((m) => (
                <a
                  key={m.id}
                  href={`/deal/${m.room_slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-lg border border-[var(--line,#E2E8F0)] bg-white p-3 hover:shadow-sm transition"
                  data-testid={`buyer-match-${m.room_slug}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <DoorOpen size={14} className="text-[var(--gold,#F59E0B)] shrink-0" />
                      <div className="truncate">
                        <div className="font-semibold text-sm truncate">Deal /{m.room_slug}</div>
                        <div className="text-[11px] text-[var(--ink-3,#94A3B8)]">{formatWhen(m.matched_at)}</div>
                      </div>
                    </div>
                    <ExternalLink size={14} className="text-[var(--ink-3,#94A3B8)] shrink-0" />
                  </div>
                </a>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", ...rest }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[var(--ink-3,#94A3B8)]">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="input"
        data-testid={testid}
        {...rest}
      />
    </label>
  );
}

function Screen({ children }) {
  return <div className="min-h-screen flex items-center justify-center gap-2 text-[var(--ink-2,#475569)]">{children}</div>;
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
